import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, ChevronDown, Upload, X } from 'lucide-react';
import cameraAddIcon from '../../../shared/assets/icons/iconstack.io - (Ic Fluent Camera Add 24 Regular).png';
import notificationIcon from '../../../shared/assets/icons/iconstack.io - (Notification Line).png';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format, parseISO } from 'date-fns';
import { useIntl } from 'react-intl';
import styles from './EditGoalModal.module.css';
import {
	deleteFinancialGoal,
	deleteGoalPicture,
	updateFinancialGoal,
	uploadGoalPicture,
} from '../../../modules/Goals/api';

// Custom Input component for DatePicker
const CustomDateInput = React.forwardRef(
	({ value, onClick, onChange, error, success, placeholder }, ref) => (
		<div
			className={`${styles.customDateInput} ${
				error ? styles.inputError : success ? styles.inputSuccess : ''
			}`}
			onClick={onClick}
			ref={ref}
		>
			<span className={value ? styles.selectedDate : styles.placeholder}>
				{value || placeholder || 'Select date'}
			</span>
		</div>
	),
);

CustomDateInput.displayName = 'CustomDateInput';

const REMIND_DAYS_WEEKLY = 7;
const REMIND_DAYS_MONTHLY = 30;

const EditGoalModal = ({
	open,
	onClose,
	goal,
	onGoalUpdated,
	onGoalDeleted,
}) => {
	const intl = useIntl();
	const [goalType, setGoalType] = useState('Primary');
	const [goalTitle, setGoalTitle] = useState('');
	const [startDate, setStartDate] = useState(new Date());
	const [dueDate, setDueDate] = useState(new Date());
	const [amount, setAmount] = useState('');
	const [currency, setCurrency] = useState('USD');
	const [notify, setNotify] = useState(false);
	const [remindFrequency, setRemindFrequency] = useState(
		intl.formatMessage({ id: 'AddRecordModalID24' }),
	);
	const [showFrequencyDropdown, setShowFrequencyDropdown] = useState(false);
	const [loading, setLoading] = useState(false);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [errors, setErrors] = useState({});
	const [fieldsHadError, setFieldsHadError] = useState({});
	const [imagePreview, setImagePreview] = useState(null);
	const [imageFile, setImageFile] = useState(null);
	const [uploadingImage, setUploadingImage] = useState(false);
	const [removingImage, setRemovingImage] = useState(false);

	const frequencies = [
		intl.formatMessage({ id: 'AddRecordModalID25' }),
		intl.formatMessage({ id: 'AddRecordModalID24' }),
	];

	const frequencyToRemindDays = freq => {
		const weeklyMsg = intl.formatMessage({ id: 'AddRecordModalID25' });
		const monthlyMsg = intl.formatMessage({ id: 'AddRecordModalID24' });
		if (freq === weeklyMsg) return REMIND_DAYS_WEEKLY;
		if (freq === monthlyMsg) return REMIND_DAYS_MONTHLY;
		return REMIND_DAYS_MONTHLY;
	};

	const remindDaysToFrequency = days => {
		if (days === REMIND_DAYS_WEEKLY) {
			return intl.formatMessage({ id: 'AddRecordModalID25' });
		}
		if (days === REMIND_DAYS_MONTHLY) {
			return intl.formatMessage({ id: 'AddRecordModalID24' });
		}
		return intl.formatMessage({ id: 'AddRecordModalID24' });
	};

	// Refs for dropdowns
	const startDateRef = useRef(null);
	const dueDateRef = useRef(null);
	const frequencyRef = useRef(null);

	useEffect(() => {
		const handleClickOutside = event => {
			if (
				frequencyRef.current &&
				!frequencyRef.current.contains(event.target)
			) {
				setShowFrequencyDropdown(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	useEffect(() => {
		if (goal) {
			const goalData = goal.originalData || goal;

			setGoalTitle(goalData.title || goal.title || '');
			setGoalType(goalData.is_default ? 'Primary' : 'Secondary');
			setAmount(goalData.target_amount || goalData.targetAmount || '');
			setCurrency(goalData.currency || 'USD');

			// Parse dates - avoid timezone issues by parsing date components directly
			if (goalData.start_date || goal.startDate) {
				const startDateValue = goalData.start_date || goal.startDate;
				try {
					let date;
					if (startDateValue.includes('T')) {
						date = parseISO(startDateValue);
					} else {
						// Parse date components directly to avoid timezone shifts
						const datePart = startDateValue.split(' ')[0]; // Handle "Dec 29, 2024" format
						const dateStr = datePart.includes('-') ? datePart : startDateValue;
						const [year, month, day] = dateStr.split('-').map(Number);
						date = new Date(year, month - 1, day);
					}
					setStartDate(date);
				} catch (error) {
					console.error('Error parsing start date:', error);
					setStartDate(new Date());
				}
			} else {
				setStartDate(new Date());
			}

			if (goalData.due_date || goal.deadline) {
				const dueDateValue = goalData.due_date || goal.deadline;
				try {
					let date;
					if (dueDateValue.includes(',')) {
						// Handle formatted date strings like "Dec 29, 2024"
						date = new Date(dueDateValue);
					} else if (dueDateValue.includes('T')) {
						date = parseISO(dueDateValue);
					} else {
						// Parse date components directly to avoid timezone shifts
						const datePart = dueDateValue.split(' ')[0]; // Handle "Dec 29, 2024" format
						const dateStr = datePart.includes('-') ? datePart : dueDateValue;
						const [year, month, day] = dateStr.split('-').map(Number);
						date = new Date(year, month - 1, day);
					}
					setDueDate(date);
				} catch (error) {
					console.error('Error parsing due date:', error);
					setDueDate(new Date());
				}
			} else {
				setDueDate(new Date());
			}

			const remindDaysValue = goalData.remind_me_in_days || 0;
			setNotify(remindDaysValue > 0);
			setRemindFrequency(
				remindDaysValue > 0
					? remindDaysToFrequency(remindDaysValue)
					: intl.formatMessage({ id: 'AddRecordModalID24' }),
			);
			setShowFrequencyDropdown(false);

			// Set image preview
			if (goalData.image_url) {
				const API_BASE_URL = process.env.REACT_APP_API_URL;
				let imageUrl = goalData.image_url;
				if (!imageUrl.startsWith('http')) {
					// Get base URL without /api/v1 for static files
					const baseUrl = API_BASE_URL.replace(/\/api\/v1\/?$/, '');
					imageUrl = imageUrl.startsWith('/uploads/')
						? `${baseUrl}${imageUrl}`
						: `${baseUrl}/uploads/${imageUrl}`;
				} else {
					// Strip /api/v1 from the URL if present (in case backend returns full URL with /api/v1)
					imageUrl = imageUrl.replace(/\/api\/v1\//g, '/');
				}
				setImagePreview(imageUrl);
			} else {
				setImagePreview(null);
			}
			setImageFile(null);
			setErrors({});
			setFieldsHadError({});
		}
	}, [goal]);

	const validateTitle = title => {
		const trimmedTitle = title.trim();
		if (!trimmedTitle) {
			return intl.formatMessage({ id: 'EditGoalModalErrorID3' });
		}
		if (trimmedTitle.length > 255) {
			return intl.formatMessage({ id: 'EditGoalModalErrorID4' });
		}
		return null;
	};

	const validateAmount = amountValue => {
		const amountNum = parseFloat(amountValue);
		if (!amountValue || isNaN(amountNum)) {
			return intl.formatMessage({ id: 'EditGoalModalErrorID5' });
		}
		if (amountNum <= 0) {
			return intl.formatMessage({ id: 'EditGoalModalErrorID6' });
		}
		return null;
	};

	const validateStartDate = date => {
		if (!date) {
			return intl.formatMessage({ id: 'EditGoalModalErrorID7' });
		}
		return null;
	};

	const validateDueDate = (due, start) => {
		if (!due) {
			return intl.formatMessage({ id: 'EditGoalModalErrorID8' });
		}
		if (start && due < start) {
			return intl.formatMessage({ id: 'EditGoalModalErrorID9' });
		}
		return null;
	};

	const getFieldClassName = (field, baseClass) => {
		const hasError = !!errors[field];
		const hasSuccess = fieldsHadError[field] && !hasError;
		return `${baseClass} ${hasError ? styles.inputError : ''} ${
			hasSuccess ? styles.inputSuccess : ''
		}`.trim();
	};

	const validateForm = () => {
		const newErrors = {};
		const titleError = validateTitle(goalTitle);
		if (titleError) newErrors.title = titleError;

		const amountError = validateAmount(amount);
		if (amountError) newErrors.amount = amountError;

		const startDateError = validateStartDate(startDate);
		if (startDateError) newErrors.startDate = startDateError;

		const dueDateError = validateDueDate(dueDate, startDate);
		if (dueDateError) newErrors.dueDate = dueDateError;

		setErrors(newErrors);
		if (Object.keys(newErrors).length > 0) {
			setFieldsHadError(prev => ({
				...prev,
				...Object.fromEntries(Object.keys(newErrors).map(key => [key, true])),
			}));
		}
		return Object.keys(newErrors).length === 0;
	};

	useEffect(() => {
		if (!Object.values(fieldsHadError).some(Boolean)) return;

		setErrors(prev => {
			const next = { ...prev };

			if (fieldsHadError.title) {
				const titleError = validateTitle(goalTitle);
				if (titleError) next.title = titleError;
				else delete next.title;
			}

			if (fieldsHadError.amount) {
				const amountError = validateAmount(amount);
				if (amountError) next.amount = amountError;
				else delete next.amount;
			}

			if (fieldsHadError.startDate) {
				const startDateError = validateStartDate(startDate);
				if (startDateError) next.startDate = startDateError;
				else delete next.startDate;
			}

			if (fieldsHadError.dueDate) {
				const dueDateError = validateDueDate(dueDate, startDate);
				if (dueDateError) next.dueDate = dueDateError;
				else delete next.dueDate;
			}

			return next;
		});
	}, [goalTitle, amount, startDate, dueDate, fieldsHadError]);

	const handleImageUpload = async e => {
		const file = e.target.files[0];
		if (!file) return;

		// Validate file type
		const validTypes = [
			'image/jpeg',
			'image/jpg',
			'image/png',
			'image/gif',
			'image/webp',
		];
		if (!validTypes.includes(file.type)) {
			alert(intl.formatMessage({ id: 'EditGoalModalErrorID11' }));
			return;
		}

		// Validate file size (5MB max)
		if (file.size > 5 * 1024 * 1024) {
			alert(intl.formatMessage({ id: 'EditGoalModalErrorID12' }));
			return;
		}

		setImageFile(file);
		const reader = new FileReader();
		reader.onloadend = () => {
			setImagePreview(reader.result);
		};
		reader.readAsDataURL(file);
	};

	const handleRemoveImage = async () => {
		if (!goal) return;

		setRemovingImage(true);
		try {
			const goalId = goal?.originalData?.id || goal?.id;
			await deleteGoalPicture(goalId);
			setImagePreview(null);
			setImageFile(null);
			// Image removed successfully, parent will refresh on next render
		} catch (error) {
			console.error('Error removing image:', error);
			alert(
				error.message || intl.formatMessage({ id: 'EditGoalModalErrorID13' }),
			);
		} finally {
			setRemovingImage(false);
		}
	};

	const handleUpdateGoal = async () => {
		if (!validateForm()) {
			return;
		}

		setLoading(true);

		try {
			const goalData = {
				title: goalTitle.trim(),
				target_amount: parseFloat(amount),
				currency: currency,
				start_date: format(startDate, 'yyyy-MM-dd'),
				due_date: format(dueDate, 'yyyy-MM-dd'),
				remind_me_in_days: notify ? frequencyToRemindDays(remindFrequency) : 0,
				is_default: goalType === 'Primary',
			};

			const goalId = goal?.originalData?.id || goal?.id;

			if (!goalId) {
				throw new Error(intl.formatMessage({ id: 'EditGoalModalErrorID14' }));
			}

			// Update goal first
			const responseData = await updateFinancialGoal(goalId, goalData);

			// Upload image if selected
			if (imageFile) {
				setUploadingImage(true);
				try {
					await uploadGoalPicture(goalId, imageFile);
				} catch (imageError) {
					console.error('Error uploading image:', imageError);
					// Don't fail the whole update if image upload fails
					alert(
						imageError.message ||
							intl.formatMessage({ id: 'EditGoalModalErrorID15' }),
					);
				} finally {
					setUploadingImage(false);
				}
			}

			if (onGoalUpdated) {
				onGoalUpdated(responseData);
			}

			onClose();
		} catch (error) {
			console.error('Error updating goal:', error);
			alert(
				error.message || intl.formatMessage({ id: 'EditGoalModalErrorID16' }),
			);
		} finally {
			setLoading(false);
		}
	};

	const handleDeleteGoal = async () => {
		if (!goal) return;

		setDeleteLoading(true);

		try {
			const goalId = goal?.originalData?.id || goal?.id;

			if (!goalId) {
				throw new Error(intl.formatMessage({ id: 'EditGoalModalErrorID17' }));
			}

			await deleteFinancialGoal(goalId);

			if (onGoalDeleted) {
				onGoalDeleted(goalId);
			}

			onClose();
		} catch (error) {
			console.error('Error deleting goal:', error);
		} finally {
			setDeleteLoading(false);
			setShowDeleteConfirm(false);
		}
	};

	const handleCancel = () => {
		onClose();
	};

	// Close dropdowns when clicking outside
	useEffect(() => {
		const handleClickOutside = event => {
			if (
				startDateRef.current &&
				!startDateRef.current.contains(event.target)
			) {
				// Handle if needed
			}
			if (dueDateRef.current && !dueDateRef.current.contains(event.target)) {
				// Handle if needed
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	if (!open || !goal) return null;

	return (
		<div className={styles.overlay}>
			<div className={styles.modal}>
				<button className={styles.closeButton} onClick={handleCancel}>
					<X size={24} />
				</button>

				<h2 className={styles.title}>
					{intl.formatMessage({ id: 'EditGoalModalID1' })}
				</h2>

				<div className={styles.modalContent}>
					{showDeleteConfirm ? (
						<div className={styles.deleteConfirm}>
							<AlertTriangle size={48} className={styles.warningIcon} />
							<h3 className={styles.deleteTitle}>
								{intl.formatMessage({ id: 'EditGoalModalID2' })}
							</h3>
							<p className={styles.deleteMessage}>
								{intl.formatMessage({ id: 'EditGoalModalID3' })} "
								{goalTitle || goal.title}"
								{intl.formatMessage({ id: 'EditGoalModalID4' })}
							</p>
							<div className={styles.deleteButtons}>
								<button
									className={styles.deleteConfirmButton}
									onClick={handleDeleteGoal}
									disabled={deleteLoading}
								>
									{deleteLoading
										? intl.formatMessage({ id: 'EditGoalModalID5' })
										: intl.formatMessage({ id: 'EditGoalModalID6' })}
								</button>
								<button
									className={styles.deleteCancelButton}
									onClick={() => setShowDeleteConfirm(false)}
									disabled={deleteLoading}
								>
									{intl.formatMessage({ id: 'EditGoalModalID7' })}
								</button>
							</div>
						</div>
					) : (
						<>
							<div className={styles.typeToggle}>
								<button
									className={`${styles.typeButton} ${
										goalType === 'Primary' ? styles.activeType : ''
									}`}
									onClick={() => setGoalType('Primary')}
								>
									{intl.formatMessage({ id: 'EditGoalModalID8' })}
								</button>
								<button
									className={`${styles.typeButton} ${
										goalType === 'Secondary' ? styles.activeType : ''
									}`}
									onClick={() => setGoalType('Secondary')}
								>
									{intl.formatMessage({ id: 'EditGoalModalID9' })}
								</button>
							</div>

							<div className={styles.formGroup}>
								<label className={styles.label}>
									{intl.formatMessage({ id: 'EditGoalModalID10' })}
								</label>
								<div className={styles.inputWithIcon}>
									<input
										type='text'
										value={goalTitle}
										onChange={e => setGoalTitle(e.target.value)}
										className={getFieldClassName('title', styles.input)}
										placeholder={intl.formatMessage({ id: 'AddGoalModalID5' })}
										maxLength={255}
									/>
									{/* <Smile size={20} className={styles.inputIcon} /> */}
								</div>
								{errors.title && (
									<span className={styles.errorText}> * {errors.title}</span>
								)}
							</div>

							<div className={styles.formGroup}>
								<label className={styles.label}>
									{intl.formatMessage({ id: 'EditGoalModalID12' })}
								</label>
								{imagePreview ? (
									<div className={styles.imagePreviewContainer}>
										<img
											src={imagePreview}
											alt='Goal preview'
											className={styles.imagePreview}
										/>
										<div className={styles.imageActions}>
											<label className={styles.imageUploadButton}>
												<Upload size={16} />
												{intl.formatMessage({ id: 'EditGoalModalID13' })}
												<input
													type='file'
													accept='image/jpeg,image/jpg,image/png,image/gif,image/webp'
													onChange={handleImageUpload}
													style={{ display: 'none' }}
													disabled={uploadingImage || removingImage}
												/>
											</label>
											<button
												className={styles.imageRemoveButton}
												onClick={handleRemoveImage}
												disabled={removingImage || uploadingImage}
												type='button'
											>
												{removingImage ? (
													intl.formatMessage({ id: 'EditGoalModalID14' })
												) : (
													<X size={16} />
												)}
											</button>
										</div>
									</div>
								) : (
									<label className={styles.imageInputField}>
										<span className={styles.imagePlaceholder}>
											{intl.formatMessage({ id: 'AddGoalModalID23' })}
										</span>
										<img
											src={cameraAddIcon}
											alt=''
											className={styles.imageFieldIcon}
											aria-hidden='true'
										/>
										<input
											type='file'
											accept='image/jpeg,image/jpg,image/png,image/gif,image/webp'
											onChange={handleImageUpload}
											className={styles.imageFileInput}
											disabled={uploadingImage}
										/>
									</label>
								)}
								{uploadingImage && (
									<p className={styles.uploadStatus}>
										{intl.formatMessage({ id: 'EditGoalModalID16' })}
									</p>
								)}
							</div>

							<div className={styles.dateRow}>
								<label className={`${styles.label} ${styles.dateStartLabel}`}>
									{intl.formatMessage({ id: 'EditGoalModalID17' })}
								</label>
								<label className={`${styles.label} ${styles.dateDueLabel}`}>
									{intl.formatMessage({ id: 'EditGoalModalID18' })}
								</label>
								<div className={styles.dateStartField} ref={startDateRef}>
									<DatePicker
										selected={startDate}
										onChange={date => setStartDate(date)}
										dateFormat='MM/dd/yyyy'
										customInput={
											<CustomDateInput
												error={errors.startDate}
												success={fieldsHadError.startDate && !errors.startDate}
												placeholder={intl.formatMessage({
													id: 'AddGoalModalID20',
												})}
											/>
										}
										popperClassName={styles.datePickerPopper}
										wrapperClassName={styles.datePickerWrapper}
										calendarClassName={styles.datePickerCalendar}
										showPopperArrow={false}
										popperPlacement='bottom-start'
										minDate={new Date()}
										maxDate={dueDate || null}
										todayButton='Today'
										showMonthDropdown
										showYearDropdown
										dropdownMode='scroll'
										scrollableYearDropdown
										yearDropdownItemNumber={15}
										isClearable
										onClear={() => setStartDate(new Date())}
										clearButtonTitle='Clear'
										className={getFieldClassName(
											'startDate',
											styles.datePickerInput,
										)}
									/>
								</div>
								<div className={styles.dateArrowCell} aria-hidden='true'>
									<span className={styles.dateArrow}>→</span>
								</div>
								<div className={styles.dateDueField} ref={dueDateRef}>
									<DatePicker
										selected={dueDate}
										onChange={date => setDueDate(date)}
										dateFormat='MM/dd/yyyy'
										customInput={
											<CustomDateInput
												error={errors.dueDate}
												success={fieldsHadError.dueDate && !errors.dueDate}
												placeholder={intl.formatMessage({
													id: 'AddGoalModalID21',
												})}
											/>
										}
										popperClassName={styles.datePickerPopper}
										wrapperClassName={styles.datePickerWrapper}
										calendarClassName={styles.datePickerCalendar}
										showPopperArrow={false}
										popperPlacement='bottom-start'
										minDate={startDate || new Date()}
										todayButton='Today'
										showMonthDropdown
										showYearDropdown
										dropdownMode='scroll'
										scrollableYearDropdown
										yearDropdownItemNumber={15}
										isClearable
										onClear={() => setDueDate(new Date())}
										clearButtonTitle='Clear'
										className={getFieldClassName(
											'dueDate',
											styles.datePickerInput,
										)}
									/>
								</div>
								{errors.startDate && (
									<span
										className={`${styles.errorText} ${styles.dateStartError}`}
									>
										{' '}
										* {errors.startDate}
									</span>
								)}
								{errors.dueDate && (
									<span
										className={`${styles.errorText} ${styles.dateDueError}`}
									>
										{' '}
										* {errors.dueDate}
									</span>
								)}
							</div>

							<div className={styles.amountRow}>
								<div className={styles.formGroup}>
									<label className={styles.label}>
										{intl.formatMessage({ id: 'EditGoalModalID19' })}
									</label>
									<input
										type='number'
										value={amount}
										onChange={e => setAmount(e.target.value)}
										className={getFieldClassName('amount', styles.input)}
										placeholder={intl.formatMessage({ id: 'AddGoalModalID22' })}
										step='0.01'
										min='0.01'
									/>
									{errors.amount && (
										<span className={styles.errorText}> * {errors.amount}</span>
									)}
								</div>
								<div className={styles.formGroup}>
									<label className={styles.label}>
										{intl.formatMessage({ id: 'EditGoalModalID20' })}
									</label>
									<div className={styles.currencySelector}>
										<select
											value={currency}
											onChange={e => setCurrency(e.target.value)}
											className={styles.currencySelect}
										>
											<option value='USD'>USD</option>
											<option value='EUR'>EUR</option>
											<option value='AMD'>AMD</option>
											<option value='RUB'>RUB</option>
										</select>
										<ChevronDown size={18} className={styles.currencyChevron} />
									</div>
								</div>
							</div>

							<div className={styles.remindRow}>
								<img
									src={notificationIcon}
									alt=''
									className={styles.bellIcon}
									aria-hidden='true'
								/>
								<div className={styles.checkboxWrapper}>
									<input
										type='checkbox'
										id='edit-goal-notify-checkbox'
										checked={notify}
										onChange={e => setNotify(e.target.checked)}
										className={styles.checkbox}
									/>
									<span className={styles.checkmark} aria-hidden='true' />
								</div>
								<label
									htmlFor='edit-goal-notify-checkbox'
									className={styles.remindLabel}
								>
									{intl.formatMessage({ id: 'AddGoalModalID16' })}
								</label>
								{notify && (
									<div className={styles.dropdownContainer} ref={frequencyRef}>
										<div
											className={styles.frequencyRow}
											onClick={() =>
												setShowFrequencyDropdown(!showFrequencyDropdown)
											}
										>
											<span className={styles.frequencyText}>
												{remindFrequency}
											</span>
											<ChevronDown size={18} />
										</div>
										{showFrequencyDropdown && (
											<div className={styles.dropdownMenu}>
												{frequencies.map(freq => (
													<div
														key={freq}
														className={styles.dropdownItem}
														onClick={() => {
															setRemindFrequency(freq);
															setShowFrequencyDropdown(false);
														}}
													>
														{freq}
													</div>
												))}
											</div>
										)}
									</div>
								)}
							</div>

							<div className={styles.buttonRow}>
								<button
									className={styles.saveButton}
									onClick={handleUpdateGoal}
									disabled={loading}
								>
									{loading
										? intl.formatMessage({ id: 'EditGoalModalID23' })
										: intl.formatMessage({ id: 'EditGoalModalID24' })}
								</button>

								<button
									className={styles.cancelButton}
									onClick={handleCancel}
									disabled={loading}
								>
									{intl.formatMessage({ id: 'EditGoalModalID25' })}
								</button>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default EditGoalModal;
